/* eslint-disable react/no-unused-prop-types,no-console,
react/no-danger,react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Check from '@material-ui/icons/Check';
import LinearProgress from '@material-ui/core/LinearProgress';
import Refresh from '@material-ui/icons/Refresh';
import Block from '@material-ui/icons/Block';
import FilterIco from '@material-ui/icons/FilterList';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Checkbox from '@material-ui/core/Checkbox';
import { Zoom, Tooltip, Hidden } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbar from 'components/UI/Snackbar/Snackbar';
// global variables
import { exchangeRateStatus } from 'config';
// our components
import { EXCHANGE_RATE, USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import dateHelper from 'helpers/dateHelper';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import ModalConfirmation from 'components/common/ModalConfirmation';
import { TableLoader } from 'components/common/TableLoader';
import Filter from 'components/ExchangeRate/Filter';
import WithPermission from 'components/User/WithPermission';

/**
 * @description - ExchangeRateList component
 * @augments React.Component
 */
class ExchangeRateList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      itemsToApplyAction: [],
      showFilters: false,
      currentAction: undefined,
      modalIsOpen: false,
    };
  }

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    // if all are selected
    if (selectedItems.length === edges.length) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({
        selectedItems: edges.map(element => element.node.id),
      });
    }
  };

  /**
   * @description
   * Select an item.
   *
   * @param {Object} node - Selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = node => {
    const { selectedItems } = this.state;
    // if item is selected
    if (selectedItems.indexOf(node.id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== node.id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, node.id] });
    }
  };

  /**
   * @description
   * Redirect to user/read route.
   *
   * @param {number} id - Id of the user to read.
   */
  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  /**
   * @description
   * Call refresh callback.
   */
  handleRefresh = () => {
    const { refresh } = this.props;
    refresh();
  };

  /**
   * @description
   * Redirect to exchange_rate/create route.
   */
  handleCreate = () => {
    const history = this.context;
    history.push(EXCHANGE_RATE.CREATE);
  };

  /**
   * @description
   * Open confirmation modal.
   *
   * @param {Array} itemsToApplyAction - Items id to apply action.
   * @param {string} currentAction - Action.
   */
  handleOpenModal = (itemsToApplyAction, currentAction) => {
    this.setState({
      modalIsOpen: true,
      itemsToApplyAction,
      currentAction,
    });
  };

  /**
   * @description
   * Callback to close confirmation modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({
      modalIsOpen: false,
      currentAction: '',
      itemsToApplyAction: [],
    });
  };

  /**
   * @description
   * Confirmation modal callback.
   * @returns {undefined} No return.
   */
  handleSubmit = () => {
    const { itemsToApplyAction, currentAction } = this.state;
    const { exchangeRateEnable, exchangeRateDisable } = this.props;

    switch (currentAction) {
      case 'enable':
        exchangeRateEnable(itemsToApplyAction);
        break;
      case 'disable':
        exchangeRateDisable(itemsToApplyAction);
        break;
      default:
        break;
    }

    this.setState({
      itemsToApplyAction: [],
      modalIsOpen: false,
    });
  };

  /**
   * @description
   * Get confirmation modal title.
   * @returns {string} Title.
   */
  getConfirmationModalTitle = () => {
    const { currentAction, itemsToApplyAction } = this.state;
    const { t } = this.props;
    let title = '';

    switch (currentAction) {
      case 'enable':
        title =
          itemsToApplyAction.length > 1
            ? t('exchange_rate.actions.enable.plural')
            : t('exchange_rate.actions.enable.singular');
        break;
      case 'disable':
        title =
          itemsToApplyAction.length > 1
            ? t('exchange_rate.actions.disable.plural')
            : t('exchange_rate.actions.disable.singular');
        break;
      default:
        break;
    }
    return title;
  };

  /**
   * @description
   * Get confirmation modal label.
   * @returns {string} Confirmation label.
   */
  getConfirmationModalConfirmLabel = () => {
    const { currentAction } = this.state;
    const { t } = this.props;
    let label = '';

    switch (currentAction) {
      case 'enable':
        label = t('common.enable');
        break;
      case 'disable':
        label = t('common.disable');
        break;
      default:
        break;
    }
    return label;
  };

  /**
   * @description
   * Get confirmation modal description.
   *
   * @param {Array} edges - Edges to get exchange rate information.
   * @returns {string} Description.
   */
  getConfirmationModalDescription = edges => {
    const { itemsToApplyAction } = this.state;
    return edges
      .filter(edge => itemsToApplyAction.indexOf(edge.node.id) !== -1)
      .map(edge => edge.node.name)
      .join(', ');
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An exchange rate.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;
    let status;

    switch (node.status) {
      case exchangeRateStatus.DISABLED:
        status = (
          <h6 className={`${classes.deletedTag}`}>
            {t('exchange_rate.list.status.disabled')}
          </h6>
        );
        break;
      case exchangeRateStatus.ENABLED:
        status = (
          <h6 className={`${classes.runningTag}`}>
            {t('exchange_rate.list.status.enabled')}
          </h6>
        );
        break;
      default:
        status = '';
        break;
    }
    return <div className={classes.infoStatusAndReview}>{status}</div>;
  };

  /**
   * @description
   * Render Date cell.
   *
   * @param {Object} node - An exchange rate.
   * @returns {*} Date cell.
   */
  renderCellDate = node => {
    const { classes, t } = this.props;
    return node.updatedBy ? (
      <div className={classes.reviewCheck}>
        <p className={classes.modificationDate}>
          {node.updatedOn && dateHelper(node.updatedOn, t)}
        </p>
        <p>
          <WithPermission
            alt={<span>{node.updatedBy && node.updatedBy.fullname}</span>}
            allowPermissions={['view_user']}
          >
            <Button
              data-cy="exchangeRateUpdatedByBtn"
              id={`pr-updatedBy-${node.updatedBy.id}`}
              className={classes.adBtns}
              simple
              size="sm"
              color="primary"
              onClick={() => this.handleReadUser(node.updatedBy.id)}
            >
              {node.updatedBy && node.updatedBy.fullname}
            </Button>
          </WithPermission>
        </p>
      </div>
    ) : (
      <div className={classes.reviewCheck}>
        <p className={classes.modificationDate}>
          {node.createdOn && dateHelper(node.createdOn, t)}
        </p>
        {node.createdBy && (
          <p>
            <WithPermission
              alt={<span>{node.createdBy && node.createdBy.fullname}</span>}
              allowPermissions={['view_user']}
            >
              <Button
                data-cy="exchangeRateCreatedByBtn"
                id={`pr-createdBy-${node.createdBy.id}`}
                className={classes.adBtns}
                simple
                size="sm"
                color="primary"
                onClick={() => this.handleReadUser(node.createdBy.id)}
              >
                {node.createdBy && node.createdBy.fullname}
              </Button>
            </WithPermission>
          </p>
        )}
      </div>
    );
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table columns.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.change')}</span>,
        accessor: 'change',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.startDate')}</span>,
        accessor: 'startDate',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.endDate')}</span>,
        accessor: 'endDate',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.rate')}</span>,
        accessor: 'rate',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.date')}</span>,
        accessor: 'date',
        sortable: false,
        filterable: false,
        minWidth: 50,
      },
      {
        Header: <span>{t('exchange_rate.list.headers.status')}</span>,
        accessor: 'status',
        sortable: false,
        filterable: false,
        maxWidth: 80,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('ad.list.headers.actions')}</p>
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        maxWidth: 80,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All exchange rates from api.
   * @returns {Array} Table rows.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    const history = this.context;
    return edges.map(({ node }) => ({
      change: (
        <div
          style={{ padding: '8px 0' }}
          id={`exchange_rate-${node.id}`}
          data-cy="exchangeRateID"
        >
          <a
            className={['read', classes.adBtns, classes.titleBtn].join(' ')}
            href={formatRoute(EXCHANGE_RATE.DETAILS, { id: node.id })}
            onClick={e => {
              e.preventDefault();
              history.push(formatRoute(EXCHANGE_RATE.DETAILS, { id: node.id }));
            }}
          >
            <div data-cy="currencyFrom">
              {`${node.currencyFrom} >> ${node.currencyTo}`}
            </div>
          </a>
        </div>
      ),
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      date: <div data-cy="date">{this.renderCellDate(node)}</div>,
      status: <div data-cy="status">{this.renderCellStatus(node)}</div>,
      startDate: <div data-cy="startDate">{node.startDate}</div>,
      endDate: <div data-cy="endDate">{node.endDate}</div>,
      rate: <div data-cy="rate">{node.rate}</div>,
      actions: selectedItems.indexOf(node.id) === -1 && (
        // we've added some custom button actions
        <div className="actions-right">
          {/* use this button to enable the data row */}
          <WithPermission allowPermissions={['change_exchangerate']}>
            {node.status === exchangeRateStatus.DISABLED && (
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.enable')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="exchangeRateEnable"
                  justIcon
                  simple
                  onClick={() => this.handleOpenModal([node.id], 'enable')}
                  className={`exchange_rate-enable-${node.id}`}
                  color="success"
                >
                  <Check />
                </Button>
              </Tooltip>
            )}
            {/* use this button to disable the data row */}
            {node.status === exchangeRateStatus.ENABLED && (
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.disable')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="exchangeRateDisable"
                  justIcon
                  simple
                  onClick={() => this.handleOpenModal([node.id], 'disable')}
                  className={`exchange_rate-disable-${node.id}`}
                  color="danger"
                >
                  <Block />
                </Button>
              </Tooltip>
            )}
          </WithPermission>
        </div>
      ),
    }));
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      t,
      failedIds,
      showNotification,
      closeNotification,
    } = this.props;
    const {
      selectedItems,
      showFilters,
      currentAction,
      modalIsOpen,
    } = this.state;
    const edges = loading || data === undefined ? [] : data.exchangeRates.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={t(`common.failed_bulk_action`, {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <ModalConfirmation
          type={currentAction === 'enable' ? 'success' : 'danger'}
          showWarningInfo={currentAction === 'delete'}
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseModal()}
          title={this.getConfirmationModalTitle()}
          description={this.getConfirmationModalDescription(edges)}
          confirmLabel={this.getConfirmationModalConfirmLabel()}
        />
        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('exchange_rate.list.title')}
                  </h3>
                </GridItem>
                <GridItem>
                  <Button
                    color="primary"
                    simple
                    onClick={this.handleRefresh}
                    id="refresh"
                    key="refresh"
                  >
                    <Refresh />
                    {t('ad.filters.update')}
                  </Button>
                  <WithPermission allowPermissions={['add_exchangerate']}>
                    <Button
                      data-cy="createExchangeRate"
                      color="success"
                      default
                      onClick={() => this.handleCreate()}
                      id="createExchangeRate"
                      style={{ marginLeft: 16 }}
                    >
                      {t('exchange_rate.form.create')}
                    </Button>
                  </WithPermission>
                </GridItem>
              </GridContainer>

              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer
                      justify="space-between"
                      alignItems="center"
                      className={[
                        classes.loteActionsAnimation,
                        selectedItems.length > 0 ? classes.hideFilters : '',
                      ].join(' ')}
                    >
                      <GridItem xs={12} sm={8} md={7}>
                        <div className={classes.searchFieldContainer}>
                          <Button
                            color="info"
                            round
                            simple={!showFilters}
                            // className={classes.filterBtn}
                            onClick={this.handleShowFilters}
                            id="filter"
                            key="filter"
                            data-cy="filterBtn"
                          >
                            <FilterIco />
                            <span>{t('user.list.filter')}</span>
                          </Button>
                        </div>
                        <Hidden
                          only={['sm', 'md', 'lg', 'xl']}
                          implementation="css"
                        >
                          <GridContainer>
                            <GridItem xs={12}>
                              <div
                                className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                              >
                                <Filter
                                  filter={filter}
                                  appliedFilter={appliedFilter}
                                  restartFilters={restartFilters}
                                  showFilters={this.handleShowFilters}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                        </Hidden>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.exchangeRates.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <Hidden only={['xs']} implementation="css">
                      <GridContainer>
                        <GridItem xs={12}>
                          <div
                            className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                          >
                            <Filter
                              filter={filter}
                              appliedFilter={appliedFilter}
                              restartFilters={restartFilters}
                            />
                          </div>
                        </GridItem>
                      </GridContainer>
                    </Hidden>
                    <div
                      className={[
                        classes.groupActions,
                        classes.loteActionsAnimation,
                        selectedItems.length > 0
                          ? classes.showLote
                          : classes.hideLote,
                      ].join(' ')}
                      xs={12}
                    >
                      <WithPermission
                        allowPermissions={['change_exchangerate']}
                      >
                        <Button
                          data-cy="enableLote"
                          color="success"
                          simple
                          onClick={() =>
                            this.handleOpenModal(selectedItems, 'enable')
                          }
                          id="enableExchangeRate"
                        >
                          <Check />
                          {t('common.enable')}
                        </Button>
                      </WithPermission>
                      <WithPermission
                        allowPermissions={['change_exchangerate']}
                      >
                        <Button
                          data-cy="disableLote"
                          color="danger"
                          simple
                          onClick={() =>
                            this.handleOpenModal(selectedItems, 'disable')
                          }
                          id="disableExchangeRate"
                        >
                          <Block />
                          {t('common.disable')}
                        </Button>
                      </WithPermission>
                    </div>
                    <GridContainer>
                      <GridItem xs={12}>
                        {loading ? (
                          <TableLoader />
                        ) : (
                          <ReactTable
                            data={rows}
                            filterable
                            columns={cols}
                            loading={loading}
                            pageSize={
                              edges.length < numberOfRowsSelected
                                ? edges.length
                                : numberOfRowsSelected
                            }
                            showPagination={false}
                            className={[
                              '-striped -highlight',
                              classes.tbl,
                            ].join(' ')}
                            expanded={loadingRows}
                            defaultFilterMethod={(field, row) => {
                              const id = field.pivotId || field.id;
                              return row[id] !== undefined
                                ? String(row[id]).startsWith(field.value)
                                : true;
                            }}
                            SubComponent={() => (
                              <div className={classes.loading}>
                                <LoadingAnimation />
                              </div>
                            )}
                            getTrProps={(state, rowInfo) => ({
                              style: {
                                boxShadow:
                                  rowInfo &&
                                  failedIds.indexOf(rowInfo.original.id) !== -1
                                    ? 'inset 4px 0 0 red'
                                    : '',
                              },
                            })}
                          />
                        )}
                      </GridItem>
                    </GridContainer>

                    <GridContainer justify="flex-end" alignItems="center">
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.exchangeRates.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * ExchangeRateList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param failedIds - id of items that was failed after bulk action
 * @param showNotification - prop to know if notification will be showed or not
 * @callback exchangeRateDisable - callback to disable an exchange rate
 * @callback exchangeRateEnable - callback to enable an exchange rate
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback restartFilters - prop pass to Filter
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

ExchangeRateList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  exchangeRateDisable: PropTypes.func.isRequired,
  exchangeRateEnable: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const ExchangeRateWrapper = props => <ExchangeRateList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(ExchangeRateWrapper),
);
